
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trip, ItineraryEvent } from "@/types/itinerary";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";

/**
 * Hook to fetch and subscribe to itinerary events
 */
export const useEventsFetcher = (trip: Trip | null) => {
  const [events, setEvents] = useState<ItineraryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  
  const fetchEvents = async () => {
    if (!trip?.id) return;
    
    try {
      setIsLoading(true);
      console.log("Fetching events for trip ID:", trip.id);
      
      const { data, error } = await supabase
        .from("itinerary_events")
        .select("*")
        .eq("trip_id", trip.id)
        .order("date", { ascending: true })
        .order("order_index", { ascending: true });
        
      if (error) {
        console.error("Error fetching events:", error);
        throw error;
      }
      
      console.log("Events fetched:", data);
      
      // If we have events, fetch the user names
      if (data && data.length > 0) {
        // Get all unique user IDs
        const userIds = [...new Set(data.map(event => event.created_by))];
        
        if (userIds.length > 0) {
          // Fetch user profiles
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username')
            .in('id', userIds);
            
          if (profilesError) {
            console.error("Error fetching user profiles:", profilesError);
            setEvents(data);
            setIsLoading(false);
            return;
          }
          
          // Create a map of user IDs to names
          const userMap = new Map();
          profiles?.forEach(profile => {
            userMap.set(
              profile.id, 
              profile.username || 'Unknown User'
            );
          });
          
          // Add user_name to each event
          const eventsWithUserNames = data.map(event => ({
            ...event,
            user_name: userMap.get(event.created_by) || 'Unknown User'
          }));
          
          setEvents(eventsWithUserNames);
        } else {
          setEvents(data);
        }
      } else {
        setEvents(data || []);
      }
    } catch (error) {
      console.error("Error in fetchEvents:", error);
      toast.error("Failed to load itinerary events");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (trip?.id) {
      fetchEvents();
      
      // Set up realtime subscription
      console.log("Setting up realtime subscription for trip ID:", trip.id);
      const channel = supabase
        .channel('itinerary-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'itinerary_events',
            filter: `trip_id=eq.${trip.id}`
          },
          (payload) => {
            console.log("Realtime update received:", payload);
            fetchEvents();
          }
        )
        .subscribe();
        
      return () => {
        console.log("Cleaning up realtime subscription");
        supabase.removeChannel(channel);
      };
    }
  }, [trip?.id]);
  
  return { events, setEvents, isLoading, fetchEvents };
};
